import CustomTextarea from "../../../components/Textarea/index.js";
import { UseCaseDataContext, DataContext, type DataContextId } from "../../../entities/useCaseChat/UseCase.js"
import React, { useState, type ChangeEvent, useMemo, useCallback } from 'react';
import Tabs from "../../../components/Tabs/index.js";
import { CtxGrid, CtxSubhead, CtxField } from "../../../components/UseCaseContextField/index.js";

function DataSection({ title, data }: { title: string; data: DataContext }) {
    return (
        <>
            <CtxSubhead>{title}</CtxSubhead>
            <CtxField label="Data Content" value={data.dataContentDescription} placeholder="No data content provided" full />
            <CtxField label="Data Format" value={data.dataFormats} placeholder="No data format provided" />
            <CtxField label="Data Sources" value={data.dataSources} placeholder="No data sources provided" />
            <CtxField label="Data Confidentiality" value={data.dataSensitivity} placeholder="No data confidentiality provided" />
            <CtxField label="Personal Data" value={data.personalData} placeholder="No personal data provided" />
            <CtxField label="Data Ownership" value={data.dataOwnership} placeholder="No data ownership provided" />
            <CtxField label="Data Residency" value={data.dataResidency} placeholder="No data residency provided" />
        </>
    );
}

interface UseCaseDataContextFormProps {
    formValues: UseCaseDataContext;
    onInputChange: (tab: DataContextId, field: keyof DataContext, value: string) => void;
    viewOnly?: boolean;
}

const UseCaseDataContextForm: React.FC<UseCaseDataContextFormProps> = ({
    formValues,
    onInputChange,
    viewOnly = false
}) => {
    const tabs = ["Training", "Input", "Output"];
    const [activeTab, setActiveTab] = useState<"Training" | "Input" | "Output">("Input");
    const tabData = useState<DataContext | null>(null)[0];
    const handleTabChange = (tab: string) => {
        setActiveTab(tab as "Training" | "Input" | "Output");

        if (tabData) {
            const tabKey = tab.toLowerCase() as DataContextId;
            onInputChange(tabKey, 'dataContentDescription', tabData.dataContentDescription);
            onInputChange(tabKey, 'dataFormats', tabData.dataFormats);
            onInputChange(tabKey, 'dataSources', tabData.dataSources);
            onInputChange(tabKey, 'dataSensitivity', tabData.dataSensitivity);
            onInputChange(tabKey, 'personalData', tabData.personalData);
            onInputChange(tabKey, 'dataOwnership', tabData.dataOwnership);
            onInputChange(tabKey, 'dataResidency', tabData.dataResidency);
        }
    };

    const getCurrentTabData = useCallback((): DataContext => {
        const tabKey = activeTab.toLowerCase() as DataContextId;
        const selectedTabData = formValues[tabKey];
        if (selectedTabData) {
            return selectedTabData;
        }

        return formValues.training || {
            dataContentDescription: '',
            dataFormats: '',
            dataSources: '',
            dataSensitivity: '',
            personalData: '',
            dataOwnership: '',
            dataResidency: ''
        };
    }, [activeTab, formValues]);

    const currentDisplayValues = useMemo(() => getCurrentTabData(), [getCurrentTabData]);

    const emptySection: DataContext = {
        dataContentDescription: '',
        dataFormats: '',
        dataSources: '',
        dataSensitivity: '',
        personalData: '',
        dataOwnership: '',
        dataResidency: '',
    };

    const editForm = useMemo(() => <div className="space-y-6">
        <CustomTextarea
            variant="plain"
            key={activeTab + "dataContentDescription"}
            label="Data Content"
            value={currentDisplayValues.dataContentDescription}
            onChange={(e: string | ChangeEvent<HTMLTextAreaElement>) => {
                const tabKey = activeTab.toLowerCase() as DataContextId;
                if (typeof e === 'string') {
                    onInputChange(tabKey, "dataContentDescription", e)
                } else {
                    onInputChange(tabKey, "dataContentDescription", e.target.value)
                }
            }}
            placeholder="Enter data content"

        />
        <CustomTextarea
            variant="plain"
            key={activeTab + "dataFormats"}
            label="Data Format"
            value={currentDisplayValues.dataFormats}
            onChange={(e: string | ChangeEvent<HTMLTextAreaElement>) => {
                const tabKey = activeTab.toLowerCase() as DataContextId;
                if (typeof e === 'string') {
                    onInputChange(tabKey, "dataFormats", e)
                } else {
                    onInputChange(tabKey, "dataFormats", e.target.value)
                }
            }}
            placeholder="Enter data format"

        />
        <CustomTextarea
            variant="plain"
            key={activeTab + "datasources"}
            label="Data Sources"
            value={currentDisplayValues.dataSources}
            onChange={(e: string | ChangeEvent<HTMLTextAreaElement>) => {
                const tabKey = activeTab.toLowerCase() as DataContextId;
                if (typeof e === 'string') {
                    onInputChange(tabKey, "dataSources", e)
                } else {
                    onInputChange(tabKey, "dataSources", e.target.value)
                }
            }}
            placeholder="Enter data sources"

        />
        <CustomTextarea
            key={activeTab + "dataSensitivity"}
            variant="plain"
            label="Data Confidentiality"
            value={currentDisplayValues.dataSensitivity}
            onChange={(e: string | ChangeEvent<HTMLTextAreaElement>) => {
                const tabKey = activeTab.toLowerCase() as DataContextId;
                if (typeof e === 'string') {
                    onInputChange(tabKey, "dataSensitivity", e)
                } else {
                    onInputChange(tabKey, "dataSensitivity", e.target.value)
                }
            }}
            placeholder="Enter data Confidentiality"

        />
        <CustomTextarea
            variant="plain"
            key={activeTab + "personalData"}
            label="Personal Data"
            value={currentDisplayValues.personalData}
            onChange={(e: string | ChangeEvent<HTMLTextAreaElement>) => {
                const tabKey = activeTab.toLowerCase() as DataContextId;
                if (typeof e === 'string') {
                    onInputChange(tabKey, "personalData", e)
                } else {
                    onInputChange(tabKey, "personalData", e.target.value)
                }
            }}
            placeholder="Enter personal data"

        />
        <CustomTextarea
            variant="plain"
            key={activeTab + "dataOwnership"}
            label="Data Ownership"
            value={currentDisplayValues.dataOwnership}
            onChange={(e: string | ChangeEvent<HTMLTextAreaElement>) => {
                const tabKey = activeTab.toLowerCase() as DataContextId;
                if (typeof e === 'string') {
                    onInputChange(tabKey, "dataOwnership", e)
                } else {
                    onInputChange(tabKey, "dataOwnership", e.target.value)
                }
            }}
            placeholder="Enter data ownership"

        />

        <CustomTextarea
            variant="plain"
            label="Data Residency"
            key={activeTab + "dataResidency"}
            value={currentDisplayValues.dataResidency}
            onChange={(e: string | ChangeEvent<HTMLTextAreaElement>) => {
                const tabKey = activeTab.toLowerCase() as DataContextId;
                if (typeof e === 'string') {
                    onInputChange(tabKey, "dataResidency", e)
                } else {
                    onInputChange(tabKey, "dataResidency", e.target.value)
                }
            }}
            placeholder="Enter data residency"

        />
    </div>, [currentDisplayValues, activeTab, onInputChange]);

    if (viewOnly) {
        return (
            <CtxGrid>
                <DataSection title="Training data" data={formValues.training ?? emptySection} />
                <DataSection title="Input data" data={formValues.input ?? emptySection} />
                <DataSection title="Output data" data={formValues.output ?? emptySection} />
            </CtxGrid>
        );
    }
    return (
        <div className="space-y-6 pb-2">
            <div className="mb-6">
                <Tabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} variant="default" />
            </div>
            {editForm}
        </div>
    )
}
export default UseCaseDataContextForm; 